import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

export default function DocumentConfiguration() {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [visaCategories, setVisaCategories] = useState([]);
  const [flatVisaTypes, setFlatVisaTypes] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [selectedVisaType, setSelectedVisaType] = useState('');
  const [loading, setLoading] = useState(true);

  // ---- Document Type Dialog ----
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);
  const [docFormData, setDocFormData] = useState({
    name: '',
    description: '',
    allowed_formats: 'pdf,jpg,jpeg,png',
    max_size_mb: 5,
    guidelines: '',
    allow_multiple_uploads: false,
  });

  // ---- Mapping Dialog ----
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [editingMappingId, setEditingMappingId] = useState(null);
  const [mappingFormData, setMappingFormData] = useState({
    visa_type_id: '',
    document_type_id: '',
    is_mandatory: true,
    display_order: 0,
  });

  // ---------- Initial Load ----------
  useEffect(() => {
    fetchData();
  }, []);

  // ---------- Load mappings on visa type change ----------
  useEffect(() => {
    if (selectedVisaType) {
      fetchMappings();
    } else {
      setMappings([]);
    }
  }, [selectedVisaType]);

  const fetchData = async () => {
    try {
      const [docsRes, visaRes] = await Promise.all([
        api.adminGetDocumentTypes(),
        api.adminGetVisaTypes(),
      ]);
      
      setDocumentTypes(Array.isArray(docsRes.data) ? docsRes.data : []);
      
      // Handle nested visa types structure
      const categories = Array.isArray(visaRes.data) ? visaRes.data : [];
      setVisaCategories(categories);
      
      // Flatten visa types for easier selection
      const flattened = categories.flatMap(cat => 
        (cat.visa_types || []).map(visa => ({
          ...visa,
          categoryName: cat.category
        }))
      );
      setFlatVisaTypes(flattened);

      // Select first visa type by default
      if (!selectedVisaType && flattened.length > 0) {
        setSelectedVisaType(String(flattened[0].id));
      }
    } catch (error) {
      console.error('Fetch data error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMappings = async () => {
    if (!selectedVisaType) return;
    try {
      const response = await api.adminGetDocumentMappings(selectedVisaType);
      const items = Array.isArray(response.data) ? response.data : [];
      
      // Sort by display_order
      items.sort((a, b) => {
        const A = Number.isFinite(a.display_order) ? a.display_order : 9999;
        const B = Number.isFinite(b.display_order) ? b.display_order : 9999;
        return A - B;
      });
      setMappings(items);
    } catch (error) {
      console.error('Fetch mappings error:', error);
      toast.error('Failed to load mappings');
    }
  };

  // ---------- Helpers ----------
  const resetDocForm = () => {
    setDocFormData({
      name: '',
      description: '',
      allowed_formats: 'pdf,jpg,jpeg,png',
      max_size_mb: 5,
      guidelines: '',
      allow_multiple_uploads: false,
    });
    setEditingDocId(null);
  };

  const resetMappingForm = () => {
    setMappingFormData({
      visa_type_id: selectedVisaType || '',
      document_type_id: '',
      is_mandatory: true,
      display_order: 0,
    });
    setEditingMappingId(null);
  };

  // ---------- Validations ----------
  const canSubmitDoc = useMemo(() => {
    if (!docFormData.name?.trim()) return false;
    if (!docFormData.description?.trim()) return false;
    if (!`${docFormData.allowed_formats}`.trim()) return false;
    const size = parseFloat(docFormData.max_size_mb);
    return !Number.isNaN(size) && size > 0;
  }, [docFormData]);

  const canSubmitMapping = useMemo(() => {
    if (!mappingFormData.visa_type_id) return false;
    if (!mappingFormData.document_type_id) return false;
    const order = parseInt(mappingFormData.display_order, 10);
    return !Number.isNaN(order) && order >= 0;
  }, [mappingFormData]);

  // ---------- Document Type CRUD ----------
  const handleDocSubmit = async (e) => {
    e.preventDefault();

    const formats = `${docFormData.allowed_formats}`
      .split(',')
      .map((f) => f.trim())
      .filter(Boolean);

    const size = parseFloat(docFormData.max_size_mb);
    const data = {
      name: docFormData.name.trim(),
      description: docFormData.description.trim(),
      allowed_formats: formats,
      max_size_mb: Number.isNaN(size) ? 5 : size,
      guidelines: docFormData.guidelines || '',
      allow_multiple_uploads: !!docFormData.allow_multiple_uploads,
    };

    try {
      if (editingDocId) {
        console.log('Updating document type:', editingDocId, data);
        await api.adminUpdateDocumentType(editingDocId, data);
        toast.success('Document type updated');
      } else {
        console.log('Creating document type:', data);
        await api.adminCreateDocumentType(data);
        toast.success('Document type created');
      }
      setDocDialogOpen(false);
      resetDocForm();
      await fetchData();
    } catch (error) {
      console.error('Document submit error:', error);
      toast.error(error?.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEditDoc = (doc) => {
    console.log('Editing document:', doc);
    setEditingDocId(doc.id);
    setDocFormData({
      name: doc.name || '',
      description: doc.description || '',
      allowed_formats: (doc.allowed_formats || []).join(','),
      max_size_mb: doc.max_size_mb ?? 5,
      guidelines: doc.guidelines || '',
      allow_multiple_uploads: !!doc.allow_multiple_uploads,
    });
    setDocDialogOpen(true);
  };

  const handleDeleteDoc = async (id) => {
    if (!window.confirm('Delete this document type?')) return;
    try {
      await api.adminDeleteDocumentType(id);
      toast.success('Document type deleted');
      await fetchData();
    } catch (error) {
      console.error('Delete document error:', error);
      toast.error('Delete failed');
    }
  };

  // ---------- Mapping CRUD ----------
  const handleMappingSubmit = async (e) => {
    e.preventDefault();

    const order = parseInt(mappingFormData.display_order, 10);
    const payload = {
      visa_type_id: mappingFormData.visa_type_id,
      document_type_id: mappingFormData.document_type_id,
      is_mandatory: !!mappingFormData.is_mandatory,
      display_order: Number.isNaN(order) ? 0 : order,
    };

    try {
      if (editingMappingId) {
        console.log('Updating mapping:', editingMappingId, payload);
        await api.adminUpdateDocumentMapping(editingMappingId, payload);
        toast.success('Document mapping updated');
      } else {
        console.log('Creating mapping:', payload);
        await api.adminCreateDocumentMapping(payload);
        toast.success('Document mapped to visa type');
      }
      setMappingDialogOpen(false);
      resetMappingForm();
      await fetchMappings();
    } catch (error) {
      console.error('Mapping submit error:', error);
      toast.error(error?.response?.data?.detail || 'Mapping failed');
    }
  };

  const handleEditMapping = (mapping) => {
    console.log('Editing mapping:', mapping);
    setEditingMappingId(mapping.id);
    setMappingFormData({
      visa_type_id: String(mapping.visa_type_id),
      document_type_id: String(mapping.document_type_id),
      is_mandatory: !!mapping.is_mandatory,
      display_order: Number.isFinite(mapping.display_order) ? mapping.display_order : 0,
    });
    setMappingDialogOpen(true);
  };

  const handleDeleteMapping = async (id) => {
    if (!window.confirm('Remove this document mapping?')) return;
    try {
      await api.adminDeleteDocumentMapping(id);
      toast.success('Mapping removed');
      await fetchMappings();
    } catch (error) {
      console.error('Delete mapping error:', error);
      toast.error('Delete failed');
    }
  };

  // ---------- Render ----------
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Document Configuration</h2>
        </div>

        <Tabs defaultValue="types" className="w-full">
          <TabsList>
            <TabsTrigger value="types">Document Types</TabsTrigger>
            <TabsTrigger value="mappings">Visa-Document Mappings</TabsTrigger>
          </TabsList>

          {/* ===== Document Types Tab ===== */}
          <TabsContent value="types">
            <div className="space-y-4">
              <div className="flex justify-end">
                <Dialog 
                  open={docDialogOpen} 
                  onOpenChange={(open) => {
                    setDocDialogOpen(open);
                    if (!open) resetDocForm();
                  }}
                >
                  <DialogTrigger asChild>
                    <Button
                      onClick={resetDocForm}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Add Document Type
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingDocId ? 'Edit' : 'Add'} Document Type</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleDocSubmit} className="space-y-4">
                      <div>
                        <Label>Document Name *</Label>
                        <Input
                          required
                          value={docFormData.name}
                          onChange={(e) => setDocFormData({ ...docFormData, name: e.target.value })}
                          placeholder="e.g., Passport Bio Page"
                        />
                      </div>
                      <div>
                        <Label>Description *</Label>
                        <Textarea
                          required
                          value={docFormData.description}
                          onChange={(e) => setDocFormData({ ...docFormData, description: e.target.value })}
                          placeholder="Describe what this document is"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Allowed Formats (comma-separated) *</Label>
                          <Input
                            required
                            value={docFormData.allowed_formats}
                            onChange={(e) => setDocFormData({ ...docFormData, allowed_formats: e.target.value })}
                            placeholder="pdf,jpg,png"
                          />
                        </div>
                        <div>
                          <Label>Max Size (MB) *</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            required
                            value={docFormData.max_size_mb}
                            onChange={(e) => setDocFormData({ ...docFormData, max_size_mb: e.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Upload Guidelines</Label>
                        <Textarea
                          value={docFormData.guidelines}
                          onChange={(e) => setDocFormData({ ...docFormData, guidelines: e.target.value })}
                          placeholder="Instructions for users"
                        />
                      </div>
                      <div className="flex items-center justify-between border rounded-lg p-3">
                        <div>
                          <Label className="font-medium">Allow Multiple Uploads</Label>
                          <p className="text-xs text-gray-500">
                            Enable this if users can upload more than one file for this document
                          </p>
                        </div>
                        <Switch
                          checked={!!docFormData.allow_multiple_uploads}
                          onCheckedChange={(checked) =>
                            setDocFormData((prev) => ({
                              ...prev,
                              allow_multiple_uploads: checked,
                            }))
                          }
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                        disabled={!canSubmitDoc}
                      >
                        {editingDocId ? 'Update' : 'Create'} Document Type
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                {documentTypes.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Formats
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Max Size
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {documentTypes.map((doc) => (
                          <tr key={doc.id}>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">{doc.name}</div>
                              <div className="text-sm text-gray-500">{doc.description}</div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {(doc.allowed_formats || []).join(', ')}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {doc.max_size_mb} MB
                            </td>
                            <td className="px-6 py-4 text-sm space-x-2">
                              <Button
                                onClick={() => handleEditDoc(doc)}
                                variant="outline"
                                size="sm"
                              >
                                Edit
                              </Button>
                              <Button
                                onClick={() => handleDeleteDoc(doc.id)}
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-200"
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-12 text-center text-gray-500">
                    No document types found. Add one to get started.
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ===== Mappings Tab ===== */}
          <TabsContent value="mappings">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="w-80">
                  <Select
                    value={selectedVisaType}
                    onValueChange={(val) => setSelectedVisaType(val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Visa Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {visaCategories.map((category) => (
                        <React.Fragment key={category.category}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase">
                            {category.category}
                          </div>
                          {(category.visa_types || []).map((visa) => (
                            <SelectItem key={visa.id} value={String(visa.id)}>
                              {visa.name}
                            </SelectItem>
                          ))}
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedVisaType && (
                  <Dialog
                    open={mappingDialogOpen}
                    onOpenChange={(open) => {
                      setMappingDialogOpen(open);
                      if (!open) resetMappingForm();
                    }}
                  >
                    <DialogTrigger asChild>
                      <Button
                        onClick={resetMappingForm}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Add Document to Visa Type
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>
                          {editingMappingId ? 'Edit' : 'Add'} Document Mapping
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleMappingSubmit} className="space-y-4">
                        <div>
                          <Label>Visa Type *</Label>
                          <Select
                            value={mappingFormData.visa_type_id}
                            onValueChange={(value) =>
                              setMappingFormData((p) => ({ ...p, visa_type_id: value }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select visa type" />
                            </SelectTrigger>
                            <SelectContent>
                              {flatVisaTypes.map((visa) => (
                                <SelectItem key={visa.id} value={String(visa.id)}>
                                  {visa.name} ({visa.categoryName})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Document Type *</Label>
                          <Select
                            value={mappingFormData.document_type_id}
                            onValueChange={(value) =>
                              setMappingFormData((p) => ({ ...p, document_type_id: value }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select document" />
                            </SelectTrigger>
                            <SelectContent>
                              {documentTypes.map((doc) => (
                                <SelectItem key={doc.id} value={String(doc.id)}>
                                  {doc.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={!!mappingFormData.is_mandatory}
                            onCheckedChange={(checked) =>
                              setMappingFormData((p) => ({ ...p, is_mandatory: checked }))
                            }
                          />
                          <Label>Mandatory Document</Label>
                        </div>

                        <div>
                          <Label>Display Order</Label>
                          <Input
                            type="number"
                            min="0"
                            value={mappingFormData.display_order}
                            onChange={(e) =>
                              setMappingFormData((p) => ({
                                ...p,
                                display_order: e.target.value,
                              }))
                            }
                          />
                        </div>

                        <Button
                          type="submit"
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                          disabled={!canSubmitMapping}
                        >
                          {editingMappingId ? 'Update' : 'Add'} Mapping
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              {selectedVisaType && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  {mappings.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Document
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Order
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {mappings.map((mapping) => (
                            <tr key={mapping.id}>
                              <td className="px-6 py-4 text-sm text-gray-900">
                                {mapping.document_type?.name || 'Unknown'}
                              </td>
                              <td className="px-6 py-4">
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    mapping.is_mandatory
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {mapping.is_mandatory ? 'Mandatory' : 'Optional'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600">
                                {Number.isFinite(mapping.display_order)
                                  ? mapping.display_order
                                  : '-'}
                              </td>
                              <td className="px-6 py-4 space-x-2">
                                <Button
                                  onClick={() => handleEditMapping(mapping)}
                                  variant="outline"
                                  size="sm"
                                >
                                  Edit
                                </Button>
                                <Button
                                  onClick={() => handleDeleteMapping(mapping.id)}
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 border-red-200"
                                >
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="p-12 text-center text-gray-500">
                      No documents mapped to this visa type yet.
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}